import React, { useState, useEffect } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import NovaSimulacao from './NovaSimulacao';
import './SimulacoesOverview.css';
import { formatarDataInteligente } from './utils/formatarData';
import API_CONFIG from './config/api';
import { montarHistorico } from './utils/historicoSimulacao';

const Simulacoes = ({ ficha, onClose, onEditarFicha }) => {
  const [simulacoes, setSimulacoes] = useState([]);
  const [selectedSimId, setSelectedSimId] = useState(null);
  const [bancos, setBancos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNovaSimulacao, setShowNovaSimulacao] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [expandedHeaderMobile, setExpandedHeaderMobile] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [fotoFullScreen, setFotoFullScreen] = useState(null);
  const [selectedParcelaIndex, setSelectedParcelaIndex] = useState({}); // Controlar qual parcela está selecionada por banco

  // GAP configurado do usuário logado (aplicado apenas visualmente nas parcelas)
  const usuarioLogado = JSON.parse(localStorage.getItem('user') || localStorage.getItem('usuario') || '{}');
  const gapParcela = Number(usuarioLogado?.permissoes?.configuracoes_pessoais?.gap_parcela || 0);
  const ocultarNomesBancos = usuarioLogado?.permissoes?.configuracoes_pessoais?.ocultarNomesBancos || false;
  const usuarioLogadoId = (usuarioLogado?._id || usuarioLogado?.id || usuarioLogado?.usuario_id || '').toString();

  // Estado para modal de observações
  const [showModalObservacoes, setShowModalObservacoes] = useState(false);
  const [simAtualObservacoes, setSimAtualObservacoes] = useState(null);
  const [novaObservacao, setNovaObservacao] = useState('');
  const [adicionandoObservacao, setAdicionandoObservacao] = useState(false);
  const historicoConversa = montarHistorico(simAtualObservacoes);

  useEffect(() => {
    if (!showModalObservacoes) return;
    setSimAtualObservacoes(anterior => simulacoes.find(s => s._id === anterior?._id) || anterior);
  }, [simulacoes, showModalObservacoes]);

  const idHistorico = simAtualObservacoes?._id;
  useEffect(() => {
    if (!showModalObservacoes || !idHistorico) return;
    let ativo = true;
    const atualizar = async () => {
      try {
        const { data } = await axios.get(`${API_CONFIG.API_URL}/simulacoes/${idHistorico}`, { headers: { 'x-auth-token': localStorage.getItem('token') } });
        if (ativo && data.success) setSimulacoes(lista => lista.map(s => s._id === idHistorico ? data.data : s));
      } catch (_) { /* Preserva a conversa já carregada em falhas transitórias. */ }
    };
    atualizar();
    const intervalo = setInterval(atualizar, 10000);
    return () => { ativo = false; clearInterval(intervalo); };
  }, [showModalObservacoes, idHistorico]);

  useEffect(() => {
    if (ficha) {
      carregarSimulacoes();
      carregarBancos();
    }
  }, [ficha]);

  // Conectar via WebSocket para receber atualizações em tempo real
  useEffect(() => {
    if (!ficha) return;

    const socket = io(API_CONFIG.BASE_URL.replace('/api', ''), {
      transports: ['websocket', 'polling'],
      path: '/socket.io'
    });

    // Autenticar usuário no socket (se houver usuário logado)
    try {
      const userData = localStorage.getItem('user');
      const parsedUser = userData ? JSON.parse(userData) : null;
      if (parsedUser?._id) {
        socket.emit('authenticate', parsedUser._id);
      }
    } catch (err) {
    }

    socket.emit('join-simulacoes');

    socket.on('nova-simulacao', (novaSimulacao) => {
      // Se a nova simulação pertence à ficha atual, adiciona
      const fichaVinculadaId = novaSimulacao.ficha_id?._id || novaSimulacao.ficha_id;
      if (fichaVinculadaId && String(fichaVinculadaId) === String(ficha._id)) {
        setSimulacoes(prev => {
          const existe = prev.some(s => s._id === novaSimulacao._id);
          if (existe) return prev;
          const novo = [ novaSimulacao, ...prev ];
          novo.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          if (!selectedSimId && novo.length > 0) setSelectedSimId(novo[0]._id);
          return novo;
        });
      }
    });

    socket.on('atualizar-simulacao', (simAtualizada) => {
      const fichaVinculadaId = simAtualizada.ficha_id?._id || simAtualizada.ficha_id;
      if (fichaVinculadaId && String(fichaVinculadaId) === String(ficha._id)) {
        setSimulacoes(prev => {
          const updated = prev.map(s => s._id === simAtualizada._id ? simAtualizada : s);
          updated.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          return updated;
        });
      }
    });

    socket.on('excluir-simulacao', (data) => {
      setSimulacoes(prev => prev.filter(s => s._id !== data.id));
    });

    return () => {
      socket.emit('leave-simulacoes');
      socket.disconnect();
    };
  }, [ficha]);

  // Detectar mudanças de tamanho da tela para mobile
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const carregarBancos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_CONFIG.API_URL}/bancos?ativos=true`,
        {
          headers: { 'x-auth-token': token }
        }
      );

      if (response.data.success) {
        // Se não houver bancos cadastrados, usar padrões
        const bancosCarregados = response.data.data.length > 0 
          ? response.data.data 
          : [
              { nome: 'Santander', _id: '1' },
              { nome: 'Itaú', _id: '2' },
              { nome: 'BV', _id: '3' },
              { nome: 'PAN', _id: '4' },
              { nome: 'C6', _id: '5' }
            ];
        setBancos(bancosCarregados);
      }
    } catch (error) {
      // Usar bancos padrão em caso de erro
      setBancos([
        { nome: 'Santander', _id: '1' },
        { nome: 'Itaú', _id: '2' },
        { nome: 'BV', _id: '3' },
        { nome: 'PAN', _id: '4' },
        { nome: 'C6', _id: '5' }
      ]);
    }
  };

  const carregarSimulacoes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_CONFIG.API_URL}/simulacoes?ficha_id=${ficha._id}`,
        {
          headers: { 'x-auth-token': token }
        }
      );

      if (response.data.success) {
        const lista = Array.isArray(response.data.data) ? response.data.data : [];
        const ordenado = lista.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setSimulacoes(ordenado);
        if (ordenado.length > 0) setSelectedSimId(ordenado[0]._id);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };


  const handleAtualizarSelection = async () => {
    if (!selectedSimId) return;
    setUpdateLoading(true);
    setUpdateSuccess(false);
    const start = Date.now();
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get(`${API_CONFIG.API_URL}/simulacoes/${selectedSimId}`, { headers: { 'x-auth-token': token } });
      if (resp.data && resp.data.success && resp.data.data) {
        const simAtualizada = resp.data.data;
        setSimulacoes(prev => {
          const existe = prev.some(s => s._id === simAtualizada._id);
          let novo = existe ? prev.map(s => s._id === simAtualizada._id ? simAtualizada : s) : [simAtualizada, ...prev];
          novo.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          return novo;
        });
      }
    } catch (err) {
      await carregarSimulacoes();
    } finally {
      const elapsed = Date.now() - start;
      const wait = Math.max(0, 1000 - elapsed);
      await new Promise((r) => setTimeout(r, wait));
      // Mostrar texto de sucesso por um curto período antes de reverter
      setUpdateLoading(false);
      setUpdateSuccess(true);
      await new Promise((r) => setTimeout(r, 800));
      setUpdateSuccess(false);
    }
  };

  const abrirObservacoes = (sim) => {
    setSimAtualObservacoes(sim);
    setShowModalObservacoes(true);
    setNovaObservacao('');
  };

  const adicionarObservacao = async () => {
    if (!novaObservacao.trim() || !simAtualObservacoes) return;

    try {
      setAdicionandoObservacao(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API_CONFIG.API_URL}/simulacoes/${simAtualObservacoes._id}/observacoes`,
        { texto: novaObservacao },
        { headers: { 'x-auth-token': token } }
      );

      if (response.data.success) {
        // Atualizar a simulação com as novas observações
        setSimulacoes(prev => prev.map(s => 
          s._id === simAtualObservacoes._id 
            ? { ...s, observacoes: response.data.observacoes, historico: response.data.historico || s.historico }
            : s
        ));
        setSimAtualObservacoes(prev => ({
          ...prev,
          observacoes: response.data.observacoes
        }));
        setNovaObservacao('');
      }
    } catch (error) {
      console.error('Erro ao adicionar observação:', error);
    } finally {
      setAdicionandoObservacao(false);
    }
  };

  const ehMinhaObservacao = (obs) => {
    const obsUserId = (obs?.usuario_id?._id || obs?.usuario_id?.id || obs?.usuario_id || '').toString();
    if (usuarioLogadoId && obsUserId) return obsUserId === usuarioLogadoId;
    return Boolean(usuarioLogado?.nome && obs?.usuario_nome && usuarioLogado.nome.trim().toLowerCase() === obs.usuario_nome.trim().toLowerCase());
  };

  const formatarMoeda = (valor) => {
    if (!valor) return 'R$ 0,00';
    return parseFloat(valor).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatarMoedaSemCentavos = (valor) => {
    if (!valor) return 'R$ 0';
    return parseFloat(valor).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const formatarCentavos = (valor) => {
    const n = parseInt(valor || 0, 10);
    return `R$ ${(n / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Formatar valor sem R$, apenas número (para parcela)
  const formatarSomenteNumero = (valor) => {
    const n = parseInt(String(valor || 0).replace(/\D/g, ''), 10);
    let reais = n / 100;
    if (gapParcela > 0) {
      reais += gapParcela;
    }
    // Arredondar para cima (Math.ceil) e sem centavos
    const arredondado = Math.ceil(reais);
    return arredondado.toLocaleString('pt-BR');
  };

  return (
    <>
      <div className="modal-overlay-preencher" onClick={(e) => e.stopPropagation()} style={{ padding: 0 }}>
        <div className="modal-preencher-container so-dialog" role="dialog" aria-modal="true" aria-label="Simulações de financiamento" style={{ maxWidth: '100%', maxHeight: '100%', width: '100%', height: '100%', borderRadius: 0 }}>
          <header className="so-header">
            <div><small>VETIM · FINANCIAMENTO</small><h2>Simulações</h2><p>Acompanhe os veículos e compare as propostas dos bancos.</p></div>
            <button type="button" onClick={onClose} aria-label="Fechar simulações">×</button>
          </header>
          <section className="so-client" aria-label="Dados do cliente">
            <div className="so-avatar" aria-hidden="true">{(ficha.cliente?.nome || 'C').slice(0, 1)}</div>
            <div><small>CLIENTE{ficha.fichaId ? ` · FICHA ${ficha.fichaId}` : ''}</small><strong>{ficha.cliente?.nome}</strong><p>CPF: {ficha.cliente?.cpf} <span>·</span> {ficha.cliente?.celular || ficha.cliente?.telefone || 'Telefone não informado'}</p></div>
            <span className="so-count">{simulacoes.length} {simulacoes.length === 1 ? 'simulação' : 'simulações'}</span>
          </section>

          <div className="modal-section-content">
            <div className="so-toolbar">
              {/* Dropdown ocupando espaço total - Visibilidade condicional mantendo espaço */}
              <select
                aria-label="Selecionar simulação"
                value={selectedSimId || ''}
                onChange={(e) => setSelectedSimId(e.target.value || null)}
                style={{ 
                  flex: 1,
                  visibility: simulacoes.length > 0 ? 'visible' : 'hidden',
                  padding: '10px 12px', 
                  borderRadius: '8px', 
                  border: '1px solid #e0e0e0', 
                  background: 'white', 
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#333',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                  backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23333%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-7.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22/%3E%3C/svg%3E")',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 12px top 50%',
                  backgroundSize: '12px auto',
                  paddingRight: '36px'
                }}
              >
                {simulacoes.map(sim => {
                  const dateLabel = formatarDataInteligente(sim.createdAt);
                  const veiculo = sim.veiculo || 'Veículo';
                  const anoInfo = sim.ano || '';
                  const labelVeiculo = anoInfo ? `${veiculo} ${anoInfo}` : veiculo;
                  return (
                    <option key={sim._id} value={sim._id}>
                      {`${labelVeiculo} — ${dateLabel}`}
                    </option>
                  );
                })}
              </select>

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                {/* Botão Atualizar: atualiza apenas a simulação selecionada */}
                <button
                  className="btn-primary sim-btn"
                  onClick={handleAtualizarSelection}
                  disabled={loading || updateLoading || updateSuccess}
                  title="Atualizar seleção"
                  style={{ minWidth: '120px' }}
                >
                  <span className="btn-content">{updateLoading ? '...' : (updateSuccess ? '✓ OK' : '⟳ Atualizar')}</span>
                </button>

                <button 
                  className="btn-primary sim-btn"
                  onClick={() => setShowNovaSimulacao(true)}
                  style={{ minWidth: '140px' }}
                >
                  <span className="btn-content">+ Nova simulação</span>
                </button>
              </div>
            </div>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                  Carregando simulações...
                </div>
              ) : simulacoes.length === 0 ? (
                <div className="lista-vazia" style={{ textAlign: 'center', padding: '3rem', color: '#999', background: '#f8f9fa', borderRadius: '8px', border: '2px dashed #e0e0e0' }}>
                  <p style={{ margin: 0, fontSize: '1.1rem' }}>Nenhuma simulação cadastrada para esta ficha.</p>
                  <small style={{ display: 'block', marginTop: '0.5rem', color: '#666' }}>Clique em “Nova simulação” para começar</small>
                </div>
              ) : (
                <div className="table-container">
                  <table className="data-table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 8px' }}>
                    <thead>
                      <tr>
                        <th style={{ width: '100px', textAlign: 'left', background: 'transparent', border: 'none', paddingBottom: '8px', paddingLeft: '12px', paddingRight: '8px' }}>Data</th>
                        <th style={{ textAlign: 'left', background: 'transparent', border: 'none', paddingBottom: '8px', paddingLeft: '8px', paddingRight: '8px' }}>Veículo</th>
                        <th style={{ width: '90px', textAlign: 'right', background: 'transparent', border: 'none', paddingBottom: '8px', paddingLeft: '4px', paddingRight: '4px' }}>Valor Venda</th>
                        <th style={{ width: '80px', textAlign: 'right', background: 'transparent', border: 'none', paddingBottom: '8px', paddingLeft: '4px', paddingRight: '4px' }}>Entrada</th>
                        <th style={{ width: '90px', textAlign: 'right', background: 'transparent', border: 'none', paddingBottom: '8px', paddingLeft: '4px', paddingRight: '4px' }}>Financiado</th>
                        <th style={{ width: '85px', textAlign: 'center', background: 'transparent', border: 'none', paddingBottom: '8px', paddingLeft: '4px', paddingRight: '4px' }}>Status</th>
                        <th style={{ width: '50px', textAlign: 'center', background: 'transparent', border: 'none', paddingBottom: '8px', paddingLeft: '4px', paddingRight: '12px' }}>Observações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedSimId ? simulacoes.filter(s => s._id === selectedSimId) : simulacoes).map((sim) => {
                        const veiculoComAno = sim.ano 
                          ? `${sim.veiculo || 'Veículo não especificado'} ${sim.ano}`
                          : (sim.veiculo || 'Veículo não especificado');
                        
                        return (
                          <React.Fragment key={sim._id}>
                          <tr className="so-summary"
                            key={sim._id}
                            style={{
                              background: 'white',
                              cursor: 'pointer',
                              verticalAlign: 'top',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                              borderRadius: '8px',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#f5f5f5';
                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
                              e.currentTarget.style.transform = 'translateY(-2px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'white';
                              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                          >
                            <td data-label="Data" style={{ width: '100px', textAlign: 'left', verticalAlign: 'top', paddingTop: '12px', borderTopLeftRadius: '8px', borderBottomLeftRadius: '8px', border: 'none', paddingLeft: '12px', paddingRight: '8px', fontSize: '0.9rem' }} title={formatarDataInteligente(sim.createdAt)}>
                              {formatarDataInteligente(sim.createdAt)}
                            </td>
                            <td data-label="Veículo" style={{ textAlign: 'left', verticalAlign: 'top', border: 'none', paddingLeft: '8px', paddingRight: '8px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                {/* Foto do Veículo */}
                                {sim.veiculo_id?.fotos && sim.veiculo_id.fotos.length > 0 && (
                                  <div 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const url = typeof sim.veiculo_id.fotos[0] === 'string' 
                                        ? sim.veiculo_id.fotos[0] 
                                        : sim.veiculo_id.fotos[0].url;
                                      setFotoFullScreen(url);
                                    }}
                                    style={{
                                      width: '54px',
                                      height: '54px',
                                      borderRadius: '8px',
                                      overflow: 'hidden',
                                      border: '2px solid #fff',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                      flexShrink: 0,
                                      cursor: 'zoom-in',
                                      background: '#f5f5f5'
                                    }}
                                  >
                                    <img 
                                      src={typeof sim.veiculo_id.fotos[0] === 'string' ? sim.veiculo_id.fotos[0] : sim.veiculo_id.fotos[0].url} 
                                      alt={sim.veiculo} 
                                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                      onError={(e) => e.target.style.display = 'none'}
                                    />
                                  </div>
                                )}
                                <strong style={{ color: '#c14427', whiteSpace: 'normal', wordBreak: 'break-word', fontSize: '1rem' }} title={veiculoComAno + (sim.placa ? ' - ' + sim.placa.replace(/-/g, '') : '')}>{veiculoComAno + (sim.placa ? ' - ' + sim.placa.replace(/-/g, '') : '')}</strong>
                              </div>
                            </td>
                            <td data-label="Valor de venda" style={{ width: '90px', textAlign: 'right', fontWeight: 600, verticalAlign: 'top', paddingTop: '12px', border: 'none', paddingLeft: '4px', paddingRight: '4px', fontSize: '0.9rem' }} title={`Valor de Venda: ${formatarMoedaSemCentavos(sim.valorVenda)}`}>
                              {formatarMoedaSemCentavos(sim.valorVenda)}
                            </td>
                            <td data-label="Entrada" style={{ width: '80px', textAlign: 'right', verticalAlign: 'top', paddingTop: '12px', border: 'none', paddingLeft: '4px', paddingRight: '4px', fontSize: '0.9rem' }} title={`Entrada: ${formatarMoedaSemCentavos(sim.entrada)}`}>
                              {formatarMoedaSemCentavos(sim.entrada)}
                            </td>
                            <td data-label="Financiado" style={{ width: '90px', textAlign: 'right', fontWeight: 600, color: '#2e7d32', verticalAlign: 'top', paddingTop: '12px', border: 'none', paddingLeft: '4px', paddingRight: '4px', fontSize: '0.9rem' }} title={`Valor Financiado: ${formatarMoedaSemCentavos(sim.financiado)}`}>
                              {formatarMoedaSemCentavos(sim.financiado)}
                            </td>
                            <td data-label="Status" style={{ width: '85px', textAlign: 'center', verticalAlign: 'top', paddingTop: '12px', borderTopRightRadius: '8px', borderBottomRightRadius: '8px', border: 'none', paddingLeft: '4px', paddingRight: '12px' }}>
                              <span 
                                title={`Status: ${sim.status || 'Pendente'}`}
                                style={(() => {
                                  const statusBaixo = (sim.status || 'Pendente').toLowerCase();
                                  let styles = {
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    fontSize: '0.8rem',
                                    fontWeight: 700,
                                    whiteSpace: 'nowrap',
                                    display: 'inline-block',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                  };
                                  
                                  if (statusBaixo === 'aprovado') {
                                    styles.background = '#4caf50';
                                    styles.color = 'white';
                                  } else if (statusBaixo === 'recusado') {
                                    styles.background = '#f44336';
                                    styles.color = 'white';
                                  } else if (statusBaixo === 'em análise') {
                                    styles.background = '#2196f3';
                                    styles.color = 'white';
                                  } else if (statusBaixo === 'finalizada') {
                                    styles.background = '#f8bbd0';
                                    styles.color = '#c2185b';
                                  } else {
                                    styles.background = '#ff9800';
                                    styles.color = 'white';
                                  }
                                  
                                  return styles;
                                })()}
                              >
                                {sim.status || 'Pendente'}
                              </span>
                            </td>
                            <td data-label="Observações" style={{ width: '50px', textAlign: 'center', verticalAlign: 'top', paddingTop: '12px', borderTopRightRadius: '8px', borderBottomRightRadius: '8px', border: 'none', paddingLeft: '4px', paddingRight: '12px' }}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  abrirObservacoes(sim);
                                }}
                                title="Observações"
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '18px',
                                  padding: '4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '4px',
                                  transition: 'background 0.2s',
                                  color: sim.observacoes && sim.observacoes.length > 0 ? '#2196f3' : '#999'
                                }}
                                onMouseEnter={(e) => e.target.style.background = 'rgba(33, 150, 243, 0.1)'}
                                onMouseLeave={(e) => e.target.style.background = 'none'}
                              >
                                💬
                              </button>
                            </td>
                          </tr>
                          <tr className="so-banks" key={`${sim._id}-bancos`}>
                            <td colSpan={7} style={{ padding: '8px 12px', border: 'none' }}>
                              {sim.simulacoes_bancos && Object.keys(sim.simulacoes_bancos).length > 0 ? (
                                (() => {
                                  const requestedCents = (() => {
                                    const v = sim.financiado || sim.valorVenda || 0;
                                    if (typeof v === 'number') return Math.round(v * 100);
                                    if (typeof v === 'string') {
                                      const n = v.replace(/[^\d,.-]/g, '').replace(',', '.');
                                      const num = parseFloat(n);
                                      return isNaN(num) ? 0 : Math.round(num * 100);
                                    }
                                    return 0;
                                  })();

                                  // Normalizar e construir lista de bancos
                                  const bancosList = Object.entries(sim.simulacoes_bancos).map(([bancoId, dados]) => {
                                    let primeiro = {};
                                    let opcoes = [];
                                    if (Array.isArray(dados)) {
                                      primeiro = dados[0] || {};
                                      opcoes = dados.map(l => ({ qtdParcelas: l.qtdParcelas, valorParcela: l.valorParcela }));
                                    } else if (dados && typeof dados === 'object') {
                                      primeiro = { valorLiberado: dados.valorLiberado, recusada: dados.recusada, entradaMinima: dados.entradaMinima };
                                      opcoes = (dados.opcoes || []).map(op => ({ qtdParcelas: op.qtdParcelas, valorParcela: op.valorParcela }));
                                    }

                                    const bancoPop = (sim.bancos_populados || []).find(b => 
                                      String(b._id) === String(bancoId) || 
                                      b.codigo_banco === bancoId || 
                                      (b.nome && b.nome.toLowerCase() === bancoId.toLowerCase())
                                    );
                                    const bancoGlobal = (bancos || []).find(b => 
                                      String(b._id) === String(bancoId) || 
                                      b.codigo_banco === bancoId || 
                                      (b.nome && b.nome.toLowerCase() === bancoId.toLowerCase())
                                    );

                                    let nomeBanco = bancoPop?.nome || bancoGlobal?.nome;
                                    if (!nomeBanco) {
                                      if (bancoId.startsWith('banco')) {
                                        nomeBanco = bancoId.replace(/^banco/, '').replace(/([a-z])([A-Z])/g, '$1 $2');
                                      } else {
                                        nomeBanco = bancoId;
                                      }
                                    }

                                    const valorLiberadoRaw = primeiro.valorLiberado || primeiro.valorLiberado === 0 ? primeiro.valorLiberado : '';
                                    const valorLiberadoCents = valorLiberadoRaw ? parseInt(String(valorLiberadoRaw).replace(/\D/g, ''), 10) || 0 : 0;

                                    const valorParcelaRaw = opcoes[0]?.valorParcela || '0';
                                    const valorParcelaCents = parseInt(String(valorParcelaRaw).replace(/\D/g, ''), 10) || 0;
                                    const coeficiente = valorLiberadoCents > 0 ? (valorParcelaCents / valorLiberadoCents) : 0;

                                    return {
                                      bancoId,
                                      nome: nomeBanco,
                                      valorLiberadoCents,
                                      valorParcelaCents,
                                      opcoes,
                                      recusada: !!primeiro.recusada,
                                      entradaMinima: primeiro.entradaMinima,
                                      coeficiente
                                    };
                                  });

                                  const aprovados = bancosList.filter(b => !b.recusada && b.valorLiberadoCents > 0).sort((a, b) => {
                                    const aPreenche = a.valorLiberadoCents >= requestedCents;
                                    const bPreenche = b.valorLiberadoCents >= requestedCents;

                                    // Peso 1: valor financiado correspondente ao solicitado
                                    if (aPreenche && !bPreenche) return -1;
                                    if (!aPreenche && bPreenche) return 1;

                                    // Se ambos preenchem ou ambos não preenchem, mas têm valores diferentes:
                                    // Mostrar os mais próximos (maior valor liberado) primeiro
                                    if (a.valorLiberadoCents !== b.valorLiberadoCents) {
                                      return b.valorLiberadoCents - a.valorLiberadoCents;
                                    }

                                    // Se mais de 1 banco tiver o mesmo valor financiado: maior coeficiente primeiro
                                    return b.coeficiente - a.coeficiente;
                                  });

                                  const recusados = bancosList.filter(b => b.recusada).sort((a, b) => a.nome.localeCompare(b.nome));
                                  const neutros = bancosList.filter(b => !b.recusada && b.valorLiberadoCents === 0).sort((a, b) => a.nome.localeCompare(b.nome));

                                  // Todos os bancos da simulação aparecem na listagem (somatório completo!)
                                  const ordenados = [...aprovados, ...recusados, ...neutros];

                                  // Tamanho fixo e quadrado para todos os badges (aumentado em 15%)
                                  const TILE_SIZE_PX = 113;
                                  const tileStyleBase = {
                                    width: `${TILE_SIZE_PX}px`,
                                    height: `${TILE_SIZE_PX}px`,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px',
                                    padding: '10px',
                                    borderRadius: '8px',
                                    textAlign: 'center',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                                    flex: `0 0 ${TILE_SIZE_PX}px`,
                                    boxSizing: 'border-box'
                                  };

                                  return (
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch', width: '100%', overflowX: 'auto', flexWrap: 'nowrap' }}>
                                      {ordenados.map((b) => {
                                        let background = '#f7f8fa';
                                        let border = '1px solid #e5e7eb';
                                        let color = '#1f2937';

                                        if (b.recusada) {
                                          background = '#fee2e2';
                                          border = '1px solid #fca5a5';
                                          color = '#7f1d1d';
                                        } else if (b.valorLiberadoCents > 0) {
                                          if (requestedCents > 0 && b.valorLiberadoCents >= requestedCents) {
                                            background = '#dcfce7';
                                            border = '1px solid #86efac';
                                          } else if (requestedCents > 0 && b.valorLiberadoCents < requestedCents) {
                                            background = '#fff7cd';
                                            border = '1px solid #fde68a';
                                          } else {
                                            background = '#e8f0ff';
                                            border = '1px solid #bfdbfe';
                                          }
                                        }

                                        // Para bancos recusados, mostrar apenas nome e status
                                        if (b.recusada) {
                                          const tooltipRecusado = `${b.nome}\nRecusada`;
                                          return (
                                            <div key={b.bancoId} title={tooltipRecusado} style={{ ...tileStyleBase, background, border, color, minWidth: `${TILE_SIZE_PX}px`, maxWidth: `${TILE_SIZE_PX}px` }}>
                                              <div style={{ fontWeight: 700, fontSize: '0.9rem', color: color }}>{ocultarNomesBancos ? `B${bancosList.indexOf(b) + 1}` : b.nome}</div>
                                              <div style={{ marginTop: '4px', fontSize: '0.72rem', color: '#7f1d1d', fontWeight: 700 }}>Recusado</div>
                                            </div>
                                          );
                                        }

                                        // Para bancos neutros / sem proposta / em análise
                                        if (b.valorLiberadoCents === 0) {
                                          const tooltipNeutro = `${b.nome}\nEm análise / Sem proposta`;
                                          return (
                                            <div key={b.bancoId} title={tooltipNeutro} style={{ ...tileStyleBase, background: '#f8fafc', border: '1px solid #e2e8f0', color: '#475569', minWidth: `${TILE_SIZE_PX}px`, maxWidth: `${TILE_SIZE_PX}px` }}>
                                              <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#334155' }}>{ocultarNomesBancos ? `B${bancosList.indexOf(b) + 1}` : b.nome}</div>
                                              <div style={{ marginTop: '4px', fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>Em análise</div>
                                            </div>
                                          );
                                        }
                                        
                                        const opc = b.opcoes && b.opcoes.length > 0 ? b.opcoes[selectedParcelaIndex[b.bancoId] || 0] : { qtdParcelas: '-', valorParcela: '0' };
                                        const temMultiplasOpcoes = b.opcoes && b.opcoes.length > 1;
                                        
                                        // Calcular coeficiente: valorParcela / valorFinanciado
                                        const valorParceleirasCents = parseInt(String(opc.valorParcela).replace(/\D/g, ''), 10) || 0;
                                        const coeficiente = b.valorLiberadoCents > 0 ? (valorParceleirasCents / b.valorLiberadoCents).toFixed(3) : '0.000';
                                        
                                        // Calcular taxa de juros mensal
                                        const qtdParcelasNum = parseInt(opc.qtdParcelas) || 0;
                                        let taxaMensalStr = '-';
                                        if (b.valorLiberadoCents > 0 && valorParceleirasCents > 0 && qtdParcelasNum > 0) {
                                          const totalPago = valorParceleirasCents * qtdParcelasNum;
                                          const jurosTotais = totalPago - b.valorLiberadoCents;
                                          const taxaMensal = (jurosTotais / (b.valorLiberadoCents * qtdParcelasNum)) * 100;
                                          taxaMensalStr = taxaMensal.toFixed(2);
                                        }
                                        
                                        // Calcular entrada: valorVenda - valorLiberado
                                        const valorVendaCents = requestedCents + (sim.entrada ? (() => {
                                          const e = sim.entrada;
                                          if (typeof e === 'number') return Math.round(e * 100);
                                          if (typeof e === 'string') {
                                            const n = e.replace(/[^\d,.-]/g, '').replace(',', '.');
                                            const num = parseFloat(n);
                                            return isNaN(num) ? 0 : Math.round(num * 100);
                                          }
                                          return 0;
                                        })() : 0);
                                        const entradaCalc = valorVendaCents - b.valorLiberadoCents;
                                        const entradaFormatada = entradaCalc === 0 ? 'Sem entrada' : `R$ ${(entradaCalc / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                                        
                                        // Construir tooltip com entrada, taxa de juros e coeficiente
                                        let tooltipText = `${b.nome}\nEntrada: ${entradaFormatada}\nTaxa Mensal: ${taxaMensalStr}%\nCoeficiente: ${coeficiente}`;
                                        if (b.entradaMinima) {
                                          const entradaMinimaFormatada = typeof b.entradaMinima === 'string' 
                                            ? b.entradaMinima 
                                            : `R$ ${b.entradaMinima.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                                          tooltipText = `${b.nome}\nEntrada: ${entradaFormatada}\nEntrada Mínima: ${entradaMinimaFormatada}\nTaxa Mensal: ${taxaMensalStr}%\nCoeficiente: ${coeficiente}`;
                                        }
                                        
                                        // Mantemos tamanho fixo (quadrado) para cada badge
                                        return (
                                          <div key={b.bancoId} title={tooltipText} style={{ ...tileStyleBase, background, border, color, minWidth: `${TILE_SIZE_PX}px`, maxWidth: `${TILE_SIZE_PX}px`, cursor: 'pointer', whiteSpace: 'pre-wrap', position: 'relative' }}>
                                            <div style={{ fontWeight: 700, fontSize: '0.9rem', color: color }}>{ocultarNomesBancos ? `B${bancosList.indexOf(b) + 1}` : b.nome}</div>
                                            <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>{formatarCentavos(b.valorLiberadoCents)}</div>
                                            <div style={{ fontSize: '0.78rem', color: '#374151', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
                                              <span>{(opc.qtdParcelas || '-') + 'x ' + formatarSomenteNumero(opc.valorParcela)}</span>
                                              {temMultiplasOpcoes && (
                                                <select
                                                  value={selectedParcelaIndex[b.bancoId] || 0}
                                                  onChange={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedParcelaIndex(prev => ({
                                                      ...prev,
                                                      [b.bancoId]: parseInt(e.target.value)
                                                    }));
                                                  }}
                                                  onClick={(e) => e.stopPropagation()}
                                                  style={{
                                                    width: '14px',
                                                    height: '14px',
                                                    padding: '0',
                                                    border: 'none',
                                                    borderRadius: '2px',
                                                    background: 'transparent',
                                                    color: 'transparent',
                                                    fontSize: '10px',
                                                    cursor: 'pointer',
                                                    appearance: 'none',
                                                    WebkitAppearance: 'none',
                                                    MozAppearance: 'none',
                                                    backgroundImage: 'url("data:image/svg+xml;charset=utf8,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%228%22 viewBox=%220 0 12 8%22%3E%3Cpath fill=%22%23374151%22 d=%22M1 1l5 5 5-5%22/%3E%3C/svg%3E")',
                                                    backgroundRepeat: 'no-repeat',
                                                    backgroundPosition: 'center',
                                                    backgroundSize: '8px 6px',
                                                    textIndent: '-9999px'
                                                  }}
                                                >
                                                  {b.opcoes.map((opt, idx) => (
                                                    <option key={idx} value={idx} style={{ backgroundColor: '#000', color: '#fff' }}>
                                                      {opt.qtdParcelas}x
                                                    </option>
                                                  ))}
                                                </select>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}

                                    </div>
                                  );
                                })()
                              ) : (
                                <div style={{ 
                                  padding: '8px 10px', 
                                  background: '#f6f7f9', 
                                  borderRadius: '8px',
                                  border: '1px dashed #e0e4ea',
                                  textAlign: 'left',
                                  color: '#6b7280',
                                  fontSize: '0.82rem'
                                }}>
                                  Nenhuma simulação cadastrada
                                </div>
                              )}
                            </td>
                          </tr>
                        </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
          </div>

          <div className="modal-preencher-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div></div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="button" className="btn-secondary" onClick={onClose}>← Voltar</button>
            </div>
          </div>

        </div>
      </div>

      {showNovaSimulacao && (
        <NovaSimulacao
          show={showNovaSimulacao}
          onClose={() => setShowNovaSimulacao(false)}
          ficha={ficha}
          onSimulacaoCriada={() => {
            carregarSimulacoes();
            setShowNovaSimulacao(false);
          }}
        />
      )}

      {/* Modal Foto Full Screen */}
      {fotoFullScreen && (
        <div 
          onClick={() => setFotoFullScreen(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.9)',
            zIndex: 20000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out'
          }}
        >
          <img 
            src={fotoFullScreen} 
            alt="Veículo Full Screen" 
            style={{ 
              maxWidth: '95%', 
              maxHeight: '95%', 
              objectFit: 'contain',
              borderRadius: '8px',
              boxShadow: '0 4px 24px rgba(0,0,0,0.5)'
            }} 
          />
          <button 
            onClick={() => setFotoFullScreen(null)}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              fontSize: '24px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 10px rgba(0,0,0,0.3)'
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Modal de Observações */}
      {showModalObservacoes && simAtualObservacoes && (
        <div className="obs-modal-overlay" onClick={() => setShowModalObservacoes(false)}>
          <section className="observations-page obs-modal" role="dialog" aria-modal="true" aria-labelledby="observacoes-simulacao-titulo" onClick={(e) => e.stopPropagation()}>
            <header className="chat-heading">
              <div>
                <strong id="observacoes-simulacao-titulo">Histórico da Simulação</strong>
                <span>{simAtualObservacoes.nome || ficha?.cliente?.nome || 'Cliente não informado'}</span>
              </div>
              <button type="button" className="obs-close-button" onClick={() => setShowModalObservacoes(false)} aria-label="Fechar observações">×</button>
            </header>

            {/* Conteúdo */}
            <div className="obs-section">
            <div className="obs-list">
              {historicoConversa.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {historicoConversa.map((obs, idx) => (
                    <React.Fragment key={obs._id || idx}>
                    {obs.sistema ? <div className="obs-system-event"><span className="obs-system-label">Sistema</span><span><strong>{obs.usuario_nome || 'Usuário'}</strong> {obs.texto}</span></div> :
                    <div className={`obs-item${ehMinhaObservacao(obs) ? ' mine' : ''}`}>
                      <div className="obs-avatar" aria-hidden="true">
                        {(obs.usuario_nome || 'U').trim().charAt(0).toUpperCase()}
                      </div>
                      <div className="obs-bubble">
                        <div className="obs-item-meta">
                          <span className="obs-item-author">{obs.usuario_nome || 'Usuário'}</span>
                        </div>
                        <div className="obs-item-text">{obs.texto}</div>
                      </div>
                    </div>
                    }
                    {obs.marcacao && <div className={`obs-block-time${!obs.sistema && ehMinhaObservacao(obs) ? ' mine' : ''}`}><time dateTime={obs.criadoEm}>{obs.marcacao}</time></div>}
                    </React.Fragment>
                  ))}
                </div>
              ) : (
                <div className="obs-empty">Nenhum evento ou mensagem registrado.</div>
              )}
            </div>

            {/* Footer com campo de nova observação */}
            <div className="obs-input-group">
              <textarea
                value={novaObservacao}
                onChange={(e) => setNovaObservacao(e.target.value)}
                placeholder="Escreva uma mensagem..."
                rows="2"
                maxLength="2000"
                className="obs-textarea"
              />
              <button
                type="button"
                onClick={adicionarObservacao}
                disabled={adicionandoObservacao || !novaObservacao.trim()}
                className="obs-send-button"
              >
                {adicionandoObservacao ? 'Enviando...' : 'Enviar'}
              </button>
            </div>
            </div>
          </section>
        </div>
      )}
    </>
  );
};

export default Simulacoes;
